import { useEffect, useState, type FormEvent } from 'react'
import { Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Bot, Library, Play, RefreshCw, Shield } from 'lucide-react'
import { AppChrome } from '../components/AppChrome'
import { useAuth } from '../lib/auth'

type LibraryCatalog = {
  platform: Array<{ key: string; kind: string; title: string; scope: string }>
  tenant: Array<{ entry_id: string; key: string; kind: string; title: string; mode: string }>
  project: Array<{ entry_id: string; key: string; kind: string; title: string; mode: string }>
  effective: Array<{
    key: string
    kind: string
    title: string
    layers: string[]
    roles: string[]
  }>
  roles: string[]
  mission_types: string[]
  modes: string[]
}

type WorkforceSnap = {
  pack_ready: boolean
  agent_count: number
  stages: Array<{ id: string; title: string; role: string }>
  library: { platform: number; tenant: number; project: number }
  runs: Array<{
    run_id: string
    mission_type: string
    status: string
    brief: string
    current_stage?: string
    created_at?: string
  }>
}

type MissionRun = {
  run_id: string
  mission_type: string
  status: string
  brief: string
  stages: Array<{
    stage_id: string
    title: string
    role: string
    status: string
    summary?: string
    report_file: string
  }>
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  })
  if (!res.ok) throw new Error(await res.text())
  return res.json() as Promise<T>
}

export function WorkforcePage() {
  const { user, loading } = useAuth()
  const [catalog, setCatalog] = useState<LibraryCatalog | null>(null)
  const [snap, setSnap] = useState<WorkforceSnap | null>(null)
  const [activeRun, setActiveRun] = useState<MissionRun | null>(null)
  const [missionType, setMissionType] = useState('new_app')
  const [brief, setBrief] = useState('')
  const [libKind, setLibKind] = useState('skill')
  const [libKey, setLibKey] = useState('design')
  const [libTitle, setLibTitle] = useState('')
  const [libBody, setLibBody] = useState('')
  const [libMode, setLibMode] = useState('extend')
  const [libScope, setLibScope] = useState('project')
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function refresh() {
    const [lib, wf] = await Promise.all([
      api<LibraryCatalog>('/api/afos360/v1/library'),
      api<WorkforceSnap>('/api/afos360/v1/workforce'),
    ])
    setCatalog(lib)
    setSnap(wf)
  }

  useEffect(() => {
    if (user?.customer_id && user?.project_id) void refresh().catch((err) => {
      setError(err instanceof Error ? err.message : 'Load failed')
    })
  }, [user?.customer_id, user?.project_id])

  if (!loading && !user) return <Navigate to="/signin" replace />

  async function onEnsure() {
    setBusy(true)
    setError(null)
    try {
      await api('/api/afos360/v1/workforce/ensure', { method: 'POST' })
      setMessage('Workforce pack seeded with default skills and rules')
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ensure failed')
    } finally {
      setBusy(false)
    }
  }

  async function onMission(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const run = await api<MissionRun>('/api/afos360/v1/workforce/missions', {
        method: 'POST',
        body: JSON.stringify({
          mission_type: missionType,
          brief,
          run_all: true,
        }),
      })
      setActiveRun(run)
      setMessage(`Mission ${run.run_id} → ${run.status}`)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Mission failed')
    } finally {
      setBusy(false)
    }
  }

  async function onLibrary(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const entry = await api<{ entry_id: string; key: string }>('/api/afos360/v1/library', {
        method: 'POST',
        body: JSON.stringify({
          kind: libKind,
          key: libKey,
          title: libTitle || `${libMode} ${libKey}`,
          body_md: libBody,
          mode: libMode,
          scope: libScope,
          roles: libKind === 'skill' ? [libKey === 'bug_fix' || libKey === 'feature' ? 'build' : libKey] : [],
        }),
      })
      setMessage(`Library entry ${entry.entry_id} saved (${libScope}/${libMode})`)
      setLibBody('')
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Library save failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AppChrome>
      <main className="afos-page grid gap-6 lg:grid-cols-2">
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2"
        >
          <h1 className="afos-page-title">Autonomous workforce</h1>
          <p className="afos-page-lead">
            Specialist agents run design → architecture → build → test → deploy → retest →
            audit. Defaults ship as a platform library; each tenant and project can extend,
            override, or add skills and rules.
          </p>
          {message && <p className="mt-3 text-sm text-[var(--color-cyan)]">{message}</p>}
          {error && <p className="mt-3 text-sm text-[var(--color-rose)]">{error}</p>}
        </motion.section>

        <section className="space-y-4 rounded-3xl border border-[var(--color-line)] bg-black/25 p-6">
          <div className="flex items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
              <Bot className="h-5 w-5 text-[var(--color-amber)]" />
              Delivery loop
            </h2>
            <button
              type="button"
              onClick={() => void onEnsure()}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-full border border-[var(--color-line)] px-3 py-2 text-sm"
            >
              <RefreshCw className="h-4 w-4" />
              Ensure pack
            </button>
          </div>
          <p className="text-sm text-[var(--color-mist)]">
            Pack ready: {snap?.pack_ready ? 'yes' : 'no'} · Agents: {snap?.agent_count ?? 0} ·
            Library P/T/J: {snap?.library.platform ?? 0}/{snap?.library.tenant ?? 0}/
            {snap?.library.project ?? 0}
          </p>
          <ol className="space-y-2 text-sm">
            {(snap?.stages || []).map((stage) => (
              <li
                key={stage.id}
                className="flex items-center justify-between rounded-2xl border border-[var(--color-line)] px-4 py-3"
              >
                <span>{stage.title}</span>
                <span className="text-[var(--color-cyan)]">{stage.role}</span>
              </li>
            ))}
          </ol>
          <form className="space-y-3 border-t border-[var(--color-line)] pt-4" onSubmit={onMission}>
            <label className="block text-xs uppercase tracking-wider text-[var(--color-mist)]">
              Mission
              <select
                className="mt-1 w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
                value={missionType}
                onChange={(e) => setMissionType(e.target.value)}
              >
                {(catalog?.mission_types || ['new_app', 'bug_fix', 'feature']).map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
            <textarea
              className="min-h-24 w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              placeholder="Brief for the workforce (new app, bug, or feature)"
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              required
            />
            <button
              type="submit"
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--color-cyan)] px-4 py-2 font-medium text-black"
            >
              <Play className="h-4 w-4" />
              Run mission
            </button>
          </form>
        </section>

        <section className="space-y-4 rounded-3xl border border-[var(--color-line)] bg-black/25 p-6">
          <h2 className="flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
            <Library className="h-5 w-5 text-[var(--color-cyan)]" />
            Skill & rule library
          </h2>
          <p className="text-sm text-[var(--color-mist)]">
            Manage custom overlays at tenant or project scope. Modes: extend, override, add.
          </p>
          <form className="space-y-3" onSubmit={onLibrary}>
            <div className="grid grid-cols-2 gap-3">
              <select
                className="rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
                value={libScope}
                onChange={(e) => setLibScope(e.target.value)}
              >
                <option value="project">Project</option>
                <option value="tenant">Tenant</option>
              </select>
              <select
                className="rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
                value={libMode}
                onChange={(e) => setLibMode(e.target.value)}
              >
                {(catalog?.modes || ['extend', 'override', 'add']).map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select
                className="rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
                value={libKind}
                onChange={(e) => setLibKind(e.target.value)}
              >
                <option value="skill">skill</option>
                <option value="rule">rule</option>
              </select>
              <input
                className="rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
                value={libKey}
                onChange={(e) => setLibKey(e.target.value)}
                placeholder="key (e.g. design)"
                required
              />
            </div>
            <input
              className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              value={libTitle}
              onChange={(e) => setLibTitle(e.target.value)}
              placeholder="Title"
            />
            <textarea
              className="min-h-28 w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              value={libBody}
              onChange={(e) => setLibBody(e.target.value)}
              placeholder="Custom skill/rule markdown"
              required
            />
            <button
              type="submit"
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-full border border-[var(--color-line)] px-4 py-2"
            >
              <Shield className="h-4 w-4" />
              Save to library
            </button>
          </form>
          <div className="max-h-64 space-y-2 overflow-auto text-sm">
            {(catalog?.effective || []).slice(0, 24).map((row) => (
              <div
                key={`${row.kind}-${row.key}`}
                className="rounded-2xl border border-[var(--color-line)] px-3 py-2"
              >
                <div className="flex justify-between gap-2">
                  <span className="text-[var(--color-foam)]">
                    {row.kind}:{row.key}
                  </span>
                  <span className="text-[var(--color-mist)]">{row.layers.join(' → ')}</span>
                </div>
                <div className="text-[var(--color-mist)]">{row.title}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-3 rounded-3xl border border-[var(--color-line)] bg-black/25 p-6 lg:col-span-2">
          <h2 className="font-[family-name:var(--font-display)] text-xl font-bold">
            Mission reports
          </h2>
          {activeRun ? (
            <div className="space-y-2">
              <p className="text-sm text-[var(--color-mist)]">
                {activeRun.run_id} · {activeRun.mission_type} · {activeRun.status}
              </p>
              <ol className="grid gap-2 md:grid-cols-2">
                {activeRun.stages.map((stage) => (
                  <li
                    key={stage.stage_id}
                    className="rounded-2xl border border-[var(--color-line)] px-4 py-3 text-sm"
                  >
                    <div className="flex justify-between">
                      <strong>{stage.title}</strong>
                      <span className="text-[var(--color-cyan)]">{stage.status}</span>
                    </div>
                    <p className="mt-1 text-[var(--color-mist)]">{stage.summary}</p>
                    <p className="mt-1 text-xs text-[var(--color-mist)]">{stage.report_file}</p>
                  </li>
                ))}
              </ol>
            </div>
          ) : (
            <p className="text-sm text-[var(--color-mist)]">Run a mission to see stage reports.</p>
          )}
          <div className="grid gap-2 md:grid-cols-3">
            {(snap?.runs || []).map((run) => (
              <button
                key={run.run_id}
                type="button"
                className="rounded-2xl border border-[var(--color-line)] px-3 py-3 text-left text-sm"
                onClick={() => {
                  void api<MissionRun>(
                    `/api/afos360/v1/workforce/missions/${encodeURIComponent(run.run_id)}`,
                  ).then(setActiveRun)
                }}
              >
                <div className="text-[var(--color-cyan)]">{run.mission_type}</div>
                <div className="truncate">{run.brief}</div>
                <div className="text-xs text-[var(--color-mist)]">
                  {run.status} · {run.run_id}
                </div>
              </button>
            ))}
          </div>
        </section>
      </main>
    </AppChrome>
  )
}
